import { Link } from "react-router";
import { useTranslation } from "react-i18next";
import { ArrowRight } from "@untitledui/icons";
import { Badge } from "@/components/base/badges/badges";
import { ProgressBar } from "@/components/base/progress-indicators/progress-indicators";
import type { CompanyProfile, TopTradeRoute } from "@/hooks/api/use-company-profile";
import {
    formatCompanyProfileCurrency,
    formatCompanyProfileNumber,
    getTradingPatternBadge,
    getVolumeTierBadge,
} from "@/components/application/company/company-profile-badges";

export type ShipperControlHubCompanySnapshotProps = {
    profile: CompanyProfile | undefined;
    isLoading: boolean;
    error: unknown;
    /** From containers recap — reflects page filters (date, carrier, status) */
    recapTotalContainers?: number;
    /** True when date range or carrier/status filters differ from “all” defaults */
    pageFiltersActive?: boolean;
};

export function ShipperControlHubCompanySnapshot({
    profile,
    isLoading,
    error,
    recapTotalContainers,
    pageFiltersActive = false,
}: ShipperControlHubCompanySnapshotProps) {
    const { t } = useTranslation(["pages:dashboard", "common"]);

    if (isLoading) {
        return (
            <div
                className="flex min-h-0 flex-col rounded-xl bg-primary p-5 shadow-xs ring-1 ring-secondary"
                aria-busy="true"
            >
                <div className="h-4 w-40 animate-pulse rounded bg-tertiary/20" />
                <div className="mt-2 h-3 w-full max-w-md animate-pulse rounded bg-tertiary/20" />
                <div className="mt-4 h-8 w-48 animate-pulse rounded bg-tertiary/20" />
                <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
                    {[1, 2, 3, 4].map((i) => (
                        <div key={i} className="h-14 animate-pulse rounded-lg bg-secondary/80" />
                    ))}
                </div>
            </div>
        );
    }

    if (error || !profile) {
        return (
            <div className="flex min-h-0 flex-col justify-center rounded-xl bg-primary p-5 shadow-xs ring-1 ring-secondary">
                <p className="text-sm text-error-600">{t("controlHub.hubCompanySnapshot.loadError")}</p>
                <Link
                    to="/shipper/profile"
                    className="mt-3 text-sm font-medium text-brand-secondary hover:text-brand-secondary_hover"
                >
                    {t("controlHub.hubCompanySnapshot.openProfile")}
                </Link>
            </div>
        );
    }

    const tradingPattern = getTradingPatternBadge(profile.trading_pattern);
    const volumeTier = getVolumeTierBadge(profile.volume_tier);
    const topRoutes = profile.top_trade_routes.slice(0, 3);

    return (
        <div className="flex min-h-0 flex-col rounded-xl bg-primary p-5 shadow-xs ring-1 ring-secondary">
            <div>
                <h2 className="text-base font-semibold text-primary">{t("controlHub.hubCompanySnapshot.title")}</h2>
                <p className="mt-1 text-sm text-tertiary">{t("controlHub.hubCompanySnapshot.subtitle")}</p>
            </div>

            <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <p className="text-sm font-medium text-primary">{profile.company_name}</p>
                {(profile.trading_pattern !== "unknown" || profile.volume_tier !== "no_activity") && (
                    <div className="flex flex-wrap items-center gap-2">
                        {profile.trading_pattern !== "unknown" && (
                            <Badge color={tradingPattern.color} size="sm">
                                <tradingPattern.icon className="mr-1 size-3" />
                                {tradingPattern.label}
                            </Badge>
                        )}
                        {profile.volume_tier !== "no_activity" && (
                            <Badge color={volumeTier.color} size="sm">
                                {volumeTier.label}
                            </Badge>
                        )}
                    </div>
                )}
            </div>

            <div className="mt-4">
                <div className="mb-1.5 flex items-center justify-between text-xs text-tertiary">
                    <span>{t("controlHub.companyProfile.profileCompleteness")}</span>
                    <span className="font-medium text-primary tabular-nums">{profile.profile_completeness_score}%</span>
                </div>
                <ProgressBar value={profile.profile_completeness_score} min={0} max={100} />
            </div>

            <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
                <div className="rounded-lg bg-secondary/50 px-3 py-2 ring-1 ring-secondary/80">
                    <p className="text-xs text-tertiary">{t("controlHub.hubCompanySnapshot.metricActivePartners")}</p>
                    <p className="mt-0.5 text-lg font-semibold tabular-nums text-primary">{profile.active_partners}</p>
                </div>
                <div className="rounded-lg bg-secondary/50 px-3 py-2 ring-1 ring-secondary/80">
                    <p className="text-xs text-tertiary">
                        {pageFiltersActive
                            ? t("controlHub.hubCompanySnapshot.metricTotalContainersFiltered")
                            : t("controlHub.hubCompanySnapshot.metricTotalContainers")}
                    </p>
                    <p className="mt-0.5 text-lg font-semibold tabular-nums text-primary">
                        {formatCompanyProfileNumber(
                            recapTotalContainers !== undefined ? recapTotalContainers : profile.total_containers,
                        )}
                    </p>
                </div>
                <div className="rounded-lg bg-secondary/50 px-3 py-2 ring-1 ring-secondary/80">
                    <p className="text-xs text-tertiary">{t("controlHub.hubCompanySnapshot.metricUniqueRoutes")}</p>
                    <p className="mt-0.5 text-lg font-semibold tabular-nums text-primary">{profile.unique_routes}</p>
                </div>
                <div className="rounded-lg bg-secondary/50 px-3 py-2 ring-1 ring-secondary/80">
                    <p className="text-xs text-tertiary">{t("controlHub.hubCompanySnapshot.metricCommodities")}</p>
                    <p className="mt-0.5 text-lg font-semibold tabular-nums text-primary">{profile.unique_commodities}</p>
                </div>
            </div>

            {topRoutes.length > 0 && (
                <div className="mt-4 min-h-0">
                    <p className="text-xs font-medium uppercase tracking-wide text-brand-secondary">
                        {t("controlHub.hubCompanySnapshot.topRoutesTitle")}
                    </p>
                    <div className="mt-2 overflow-x-auto rounded-lg ring-1 ring-secondary">
                        <table className="w-full min-w-[280px] text-left text-sm">
                            <thead>
                                <tr className="border-b border-secondary bg-secondary/40 text-xs text-tertiary">
                                    <th className="px-3 py-2 font-medium">{t("controlHub.hubCompanySnapshot.colRoute")}</th>
                                    <th className="px-3 py-2 font-medium tabular-nums">{t("controlHub.hubCompanySnapshot.colShipments")}</th>
                                    <th className="px-3 py-2 font-medium">{t("controlHub.hubCompanySnapshot.colValue")}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {topRoutes.map((item: TopTradeRoute, index: number) => (
                                    <tr
                                        key={`${item.origin_country_code}-${item.destination_country_code}-${index}`}
                                        className="border-b border-secondary/80 last:border-0"
                                    >
                                        <td className="px-3 py-2">
                                            <span className="font-medium text-primary">{item.origin_country_code}</span>
                                            <ArrowRight className="mx-1 inline size-3.5 text-quaternary align-middle" />
                                            <span className="font-medium text-primary">{item.destination_country_code}</span>
                                        </td>
                                        <td className="px-3 py-2 tabular-nums text-tertiary">{item.shipment_count}</td>
                                        <td className="px-3 py-2 font-medium text-primary tabular-nums">
                                            {formatCompanyProfileCurrency(item.total_value_usd)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            <Link
                to="/shipper/profile"
                className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-brand-secondary hover:text-brand-secondary_hover"
            >
                {t("controlHub.hubCompanySnapshot.openProfile")}
                <ArrowRight className="size-4" />
            </Link>
        </div>
    );
}
